import { db } from "../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAt,
} from "firebase/firestore";

export async function fetchSaintDoc(id) {
  const snap = await getDoc(doc(db, "saints", id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function upsertSaint(id, payload, { merge = true } = {}) {
  const data = { ...payload, updatedAt: serverTimestamp() };
  await setDoc(doc(db, "saints", id), data, { merge });
  return data;
}

export async function createSaint(id, payload) {
  const data = {
    ...payload,
    rand: randomFloat01(),
    updatedAt: serverTimestamp(),
  };
  await setDoc(doc(collection(db, "saints"), id), data, { merge: false });
  return data;
}

export async function fetchAllSaints() {
  const snap = await getDocs(collection(db, "saints"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((s) => s?.approved !== false);
}

function randomFloat01() {
  // Better RNG than Math.random when available
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const buf = new Uint32Array(1);
    crypto.getRandomValues(buf);
    return buf[0] / 2 ** 32; // [0, 1)
  }
  return Math.random();
}

export async function getRandomSaintId({ excludeId } = {}) {
  let r = randomFloat01();
  const startForward = randomFloat01() < 0.5;
  const PAGE = 20; // scan a small batch so we can skip unapproved without extra reads

  function firstApprovedId(snap) {
    for (const d of snap.docs) {
      const data = d.data();
      if (data?.approved === false) continue; // ✅ skip unapproved
      if (excludeId && d.id === excludeId) continue; // skip current profile
      return d.id;
    }
    return null;
  }

  async function pick(forward) {
    if (forward) {
      const q1 = query(
        collection(db, "saints"),
        orderBy("rand"),
        startAt(r),
        limit(PAGE)
      );
      const s1 = await getDocs(q1);
      const id1 = firstApprovedId(s1);
      if (id1) return id1;

      const q2 = query(
        collection(db, "saints"),
        orderBy("rand"),
        startAt(0),
        limit(PAGE)
      );
      const s2 = await getDocs(q2);
      return firstApprovedId(s2);
    } else {
      const q1 = query(
        collection(db, "saints"),
        orderBy("rand", "desc"),
        startAt(r),
        limit(PAGE)
      );
      const s1 = await getDocs(q1);
      const id1 = firstApprovedId(s1);
      if (id1) return id1;

      // wrap-around to the "top" (largest rand)
      const q2 = query(
        collection(db, "saints"),
        orderBy("rand", "desc"),
        startAt(1),
        limit(PAGE)
      );
      const s2 = await getDocs(q2);
      return firstApprovedId(s2);
    }
  }

  // Try a few times (alternating direction) to avoid exclude/unapproved clusters
  for (let i = 0; i < 6; i++) {
    const forward = startForward ? i % 2 === 0 : i % 2 !== 0;
    const id = await pick(forward);
    if (id) return id;

    // re-roll start point if we only hit unapproved/excluded docs
    r = randomFloat01();
  }

  return null;
}
